// ErrorBoundary that reports errors to server via API
import React from 'react'

interface Props {
  children: React.ReactNode
  fallback?: React.ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info)
    // Report error to server
    const errorData = {
      message: error.message,
      stack: error.stack?.substring(0, 2000),
      componentStack: info.componentStack?.substring(0, 2000),
      url: window.location.href,
      timestamp: new Date().toISOString()
    }
    // Try to send to server
    fetch('/api/debug/error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(errorData)
    }).catch(() => {
      // If fetch fails, at least log to console
      console.error('Failed to report error:', errorData)
    })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      return (
        <div style={{
          padding: 48,
          textAlign: 'center',
          color: '#999',
          minHeight: 300,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h3 style={{ color: '#333', marginBottom: 8 }}>页面加载出错</h3>
          <p style={{ marginBottom: 16, maxWidth: 500, color: '#c00', fontSize: 13, fontFamily: 'monospace' }}>
            {this.state.error?.message || '未知错误'}
          </p>
          {this.state.error?.stack && (
            <details style={{ marginBottom: 16, maxWidth: 600, textAlign: 'left', fontSize: 11, color: '#666' }}>
              <summary style={{ cursor: 'pointer', color: '#999' }}>查看详细错误</summary>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#f5f5f5', padding: 12, borderRadius: 4, maxHeight: 200, overflow: 'auto' }}>
                {this.state.error.stack}
              </pre>
            </details>
          )}
          <p style={{ fontSize: 12, color: '#bbb', marginBottom: 16 }}>
            请尝试刷新页面，如果问题持续存在请联系管理员
          </p>
          <button
            onClick={this.handleReset}
            style={{
              padding: '8px 24px',
              backgroundColor: '#55266c',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer'
            }}
          >
            重试
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
